import { useState } from "react";
import { TrendingUp, Loader2, FileUp, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { nb } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const blankInvestor = () => ({ name: "", org: "", shares: "" });
const EMPTY = { date: "", share_class: "", newClass: "", price_per_share: "", resolution_date: "", note: "" };

export function EmissionUploadDialog({ companyId, classes = [], open, onOpenChange, onUploaded }) {
  const [form, setForm] = useState(EMPTY);
  const [investors, setInvestors] = useState([blankInvestor()]);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const classNames = classes.map((c) => c.name).filter(Boolean);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setInv = (i, k, v) => setInvestors((arr) => arr.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const addInv = () => setInvestors((arr) => [...arr, blankInvestor()]);
  const removeInv = (i) => setInvestors((arr) => (arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr));
  const reset = () => { setForm(EMPTY); setInvestors([blankInvestor()]); setFile(null); };

  const valid = investors.filter((x) => x.name.trim() && Number(x.shares) > 0);
  const totalShares = valid.reduce((s, x) => s + Number(x.shares), 0);

  const submit = async () => {
    const shareClass = form.share_class === "__new__" ? form.newClass.trim() : form.share_class;
    if (!file) return toast.error("Last opp emisjonspapirene (PDF)");
    if (!shareClass) return toast.error("Velg eller skriv inn aksjeklasse");
    if (!valid.length) return toast.error("Legg til minst én investor med navn og antall aksjer");
    const notes = [form.note.trim(), form.resolution_date ? `Vedtaksdato: ${form.resolution_date}` : ""]
      .filter(Boolean).join(" · ");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", "emisjon");
    fd.append("share_class", shareClass);
    fd.append("price_per_share", form.price_per_share || "");
    fd.append("note", notes);
    fd.append("subscribers", JSON.stringify(valid.map((x) => ({ name: x.name.trim(), org: x.org, shares: Number(x.shares) }))));
    setSaving(true);
    try {
      await api.post(`/companies/${companyId}/share-uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Emisjon med ${valid.length} investor${valid.length === 1 ? "" : "er"} sendt til Murphy for godkjenning`);
      onOpenChange(false);
      reset();
      onUploaded?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="emission-dialog">
        <DialogHeader>
          <DialogTitle>Manuell emisjon</DialogTitle>
          <DialogDescription>Registrer en kapitalforhøyelse manuelt og last opp emisjonspapirene. Legg til alle investorene som tegner aksjer. Murphy godkjenner og fører de nye aksjene inn i aksjeeierboka.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Emisjonspapirer (PDF)</Label>
            <label className="mt-1.5 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-3 text-sm hover:bg-muted/40" data-testid="emission-file-label">
              <FileUp className="h-4 w-4 shrink-0 text-primary" />
              <span className="truncate text-muted-foreground">{file ? file.name : "Velg PDF-fil …"}</span>
              <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="emission-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Dato</Label><Input type="date" className="mt-1.5" data-testid="emission-date" value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
            <div><Label>Vedtaksdato (styre/GF)</Label><Input type="date" className="mt-1.5" data-testid="emission-resolution-date" value={form.resolution_date} onChange={(e) => set("resolution_date", e.target.value)} /></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Aksjeklasse</Label>
              {classNames.length ? (
                <Select value={form.share_class} onValueChange={(v) => set("share_class", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="emission-class-select"><SelectValue placeholder="Velg klasse" /></SelectTrigger>
                  <SelectContent>
                    {classNames.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    <SelectItem value="__new__">+ Ny aksjeklasse …</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input className="mt-1.5" value={form.newClass} data-testid="emission-newclass-input" onChange={(e) => set("newClass", e.target.value)} placeholder="Ordinære aksjer" />
              )}
              {form.share_class === "__new__" ? (
                <Input className="mt-2" value={form.newClass} data-testid="emission-newclass-input" onChange={(e) => set("newClass", e.target.value)} placeholder="Navn på ny aksjeklasse" />
              ) : null}
            </div>
            <div><Label>Tegningskurs per aksje (kr)</Label><Input type="number" className="mt-1.5" data-testid="emission-price" value={form.price_per_share} onChange={(e) => set("price_per_share", e.target.value)} placeholder="Samme for alle" /></div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Investorer ({valid.length}) · totalt {nb(totalShares)} aksjer</Label>
              <Button type="button" size="sm" variant="outline" className="h-7 gap-1.5" data-testid="emission-add-investor" onClick={addInv}>
                <Plus className="h-3.5 w-3.5" /> Legg til investor
              </Button>
            </div>
            <div className="mt-2 space-y-2">
              {investors.map((inv, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`emission-investor-${i}`}>
                  <Input className="flex-1" placeholder="Navn (person/selskap)" data-testid={`emission-inv-name-${i}`} value={inv.name} onChange={(e) => setInv(i, "name", e.target.value)} />
                  <Input className="w-28" placeholder="Org.nr" data-testid={`emission-inv-org-${i}`} value={inv.org} onChange={(e) => setInv(i, "org", e.target.value)} />
                  <Input className="w-24" type="number" placeholder="Aksjer" data-testid={`emission-inv-shares-${i}`} value={inv.shares} onChange={(e) => setInv(i, "shares", e.target.value)} />
                  <Button type="button" size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-muted-foreground" data-testid={`emission-inv-remove-${i}`} disabled={investors.length === 1} onClick={() => removeInv(i)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div><Label>Merknad (valgfritt)</Label><Textarea className="mt-1.5" rows={2} data-testid="emission-note" value={form.note} onChange={(e) => set("note", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={submit} disabled={saving} className="gap-2" data-testid="emission-submit-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />} Send til godkjenning
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
